import { useCallback, useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";

/**
 * Datos del CRM de desarrolladoras (colección `desarrolladoras`, sin mapa) +
 * la lista de usuarios comerciales para asignar. Cache a nivel módulo para no
 * releer al cambiar de pestaña. Mismo patrón que useProspeccionData.
 */
let _cache = null;

async function fetchAll() {
  const [dSnap, comSnap] = await Promise.all([
    getDocs(collection(db, "desarrolladoras")),
    getDocs(query(collection(db, "users"), where("typeuser", "==", 4))),
  ]);
  const desarrolladoras = dSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const comerciales = comSnap.docs.map((d) => {
    const x = d.data();
    const name = x.name || `${x.NombreCompleto || ""} ${x.ApellidosCompleto || ""}`.trim();
    return { userid: x.userid, email: String(x.email || "").toLowerCase(), name: name || x.email };
  });
  return { desarrolladoras, comerciales };
}

export function useDesarrolladorasData() {
  const [state, setState] = useState(() =>
    _cache ? { ..._cache, loading: false, error: null }
           : { desarrolladoras: [], comerciales: [], loading: true, error: null }
  );

  const load = useCallback(async (force = false) => {
    if (_cache && !force) { setState({ ..._cache, loading: false, error: null }); return; }
    setState((s) => ({ ...s, loading: true }));
    try {
      _cache = await fetchAll();
      setState({ ..._cache, loading: false, error: null });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e }));
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  // Refleja un guardado/creación en el cache sin recargar todo.
  const upsertLocal = useCallback((id, data) => {
    const base = _cache || { desarrolladoras: [], comerciales: [] };
    const list = base.desarrolladoras.slice();
    const idx = list.findIndex((d) => d.id === id);
    if (idx >= 0) list[idx] = { ...list[idx], ...data, id };
    else list.unshift({ id, ...data });
    _cache = { ...base, desarrolladoras: list };
    setState({ ..._cache, loading: false, error: null });
  }, []);

  return { ...state, reload: () => load(true), upsertLocal };
}
